import React, { useState } from 'react';
import { X, FileText, DollarSign, Send, Zap } from 'lucide-react';

export const QuoteModal = ({ provider, isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    scope: '',
    budget: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-indigo-100">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex justify-between items-start text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Zap className="h-5 w-5 text-yellow-300 fill-yellow-300" />
              </div>
              <div>
                <h3 className="text-lg font-bold leading-6">Get a Quick Quote</h3>
                <p className="text-indigo-100 text-xs mt-0.5">Request a custom estimate</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="rounded-full p-1 hover:bg-white/20 transition-colors focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/50">
             <p className="text-sm text-slate-600">
               Tell <span className="font-bold text-slate-900">{provider.name}</span> about your needs to get a price estimate.
             </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            
            {/* Project Scope */}
            <div>
              <label htmlFor="scope" className="block text-sm font-bold text-slate-700 mb-1">
                What do you need done?
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-slate-400" />
                </div>
                <textarea
                  id="scope"
                  required
                  rows={4}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm shadow-sm resize-none bg-slate-50 focus:bg-white transition-all"
                  placeholder="Describe your project, deliverables, and goals..."
                  value={formData.scope}
                  onChange={(e) => setFormData({...formData, scope: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Budget */}
              <div>
                <label htmlFor="budget" className="block text-sm font-bold text-slate-700 mb-1">
                  Budget (Est.)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    id="budget"
                    className="block w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm shadow-sm bg-slate-50 focus:bg-white transition-all"
                    placeholder="e.g. 500-1k"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  />
                </div>
              </div>

               {/* Timeline */}
               <div>
                <label htmlFor="timeline" className="block text-sm font-bold text-slate-700 mb-1">
                  Timeline
                </label>
                <input
                    type="text"
                    id="timeline"
                    className="block w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm shadow-sm bg-slate-50 focus:bg-white transition-all"
                    placeholder="e.g. ASAP, 1 week"
                    value={formData.timeline}
                    onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                  />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-900 border border-transparent text-white py-3 px-4 rounded-xl text-sm font-bold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-95"
              >
                {isSubmitting ? (
                  <>Sending Request...</>
                ) : (
                  <>
                    Send Quote Request <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};